import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { IField } from '@app/dashboard/types/outerLayout/common';
import { Input, Modal } from '@unified-checkout/design-system';
import type { TCurrentTime } from 'libs/design-system/src/types/time_picker';

import type { TAppliedRanges } from '@app/dashboard/types/innerLayout/date-ranges';
import { useLangStore } from '@app/dashboard/store/lang';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import {
  CREATE_CAMPAIGN_FIELDS,
  INITIAL_ADD_EDIT_FIELDS_STATE,
} from '@app/dashboard/constants/reach/modals/create-campaign';
import type {
  IChangeField,
  ICreateCampaign,
  ICreateCampaignProps,
} from '@app/dashboard/types/innerLayout/reach/modals/create-modal';
import {
  isFormValid,
  onFieldChangeHandler,
} from '@app/dashboard/utils/formValidations';
import { formatToISODate } from '@app/dashboard/utils/dateFormats';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';
import { createCampaign } from '@app/dashboard/services/inner_layout/reach/campaigns';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES,
} from '@app/dashboard/constants/tracking_events/system';
import { notify, useNotificationStore } from '@app/dashboard/utils/notifier';
import { NOTIFICATION_STATUS } from '@app/dashboard/constants/notifier';

import { handleAmplitudeTrackingOnCreateCampaign } from '../../../../utils/create-campaign';

import UploadPhoneNumbers from './UploadPhoneNumbers';
import SmsDescription from './SmsDescription';
import SmsContent from './SmsContent';
import DatePicker from './DatePicker';

import styles from './CreateCampaign.module.scss';

const CreateCampaign = ({
  isModalOpen,
  setIsModalOpen,
  senderName,
  availableAmount,
  usedAmount,
}: ICreateCampaignProps) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const [fields, setFields] = useState<IField>(
    INITIAL_ADD_EDIT_FIELDS_STATE(t)
  );
  const containerRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const { lang } = useLangStore();
  const { show: isNotifyShown, state: notifyState } = useNotificationStore();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const handleFieldChange = ({
    event,
    fallBackName = '',
    fallBackValue = '',
  }: IChangeField) => {
    const { name, value } = event?.target || {};
    let finalValue: any = value || fallBackValue;
    let fieldName = name || fallBackName;

    onFieldChangeHandler(fields, setFields, fieldName, finalValue, () => {});
  };

  const onCloseHandler = () => {
    setIsModalOpen(false);
    setFields(INITIAL_ADD_EDIT_FIELDS_STATE(t));
  };

  const invalidateReachQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.REACH_GET_CAMPAIGNS],
    });

    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.REACH_GET_CURRENT_PLAN],
    });
  };

  const handleTimeFormat = (time: TCurrentTime) => {
    const { hour, minute, ampm } = time;
    let formattedTime;
    const hourInNumber = Number(hour);

    if (ampm === 'PM') {
      if(hourInNumber === 12) {
        formattedTime = `00:${minute}:00`;
      } else {
        formattedTime = `${hourInNumber + 12}:${minute}:00`;
      }
    } else {
      formattedTime = `${hour}:${minute}:00`;
    }

    return formattedTime;
  };

  const { mutate: handleAddEditCampaign, isPending } = useMutation({
    mutationFn: async () => {
      const phoneNumbers = fields[CREATE_CAMPAIGN_FIELDS.MOBILE_NUMBERS].value;
      const date = fields[CREATE_CAMPAIGN_FIELDS.DATE].value as TAppliedRanges;
      const time = fields[CREATE_CAMPAIGN_FIELDS.TIME].value as TCurrentTime;
      const formattedTime = handleTimeFormat(time);

      const campaignData = {
        title: fields[CREATE_CAMPAIGN_FIELDS.TITLE].value,
        description: fields[CREATE_CAMPAIGN_FIELDS.DESCRIPTION].value,
        content: fields[CREATE_CAMPAIGN_FIELDS.CONTENT].value,
        mobile_numbers: Array.isArray(phoneNumbers)
          ? phoneNumbers.map(String)
          : [],
        start_date: formatToISODate(date?.start as Date) + ' ' + formattedTime,
      };
      await createCampaign(campaignData as ICreateCampaign);
      return campaignData as ICreateCampaign;
    },
    onSuccess: (campaignData) => {
      handleAmplitudeTrackingOnCreateCampaign({
        campaignData,
        success: true,
        fields,
        senderName,
      });

      const successMsg = t(
        'innerLayout.reach.campaigns.createCampaign.createdSuccessfully'
      );
      notify.success(successMsg, {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'success',
            trackingCode:
              SYSTEM_EVENTS_GROUPING_CODES.REACH.CAMPAIGNS.CREATE_CAMPAIGN,
            msg: successMsg,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.REACH.CAMPAIGNS,
          }),
      });

      setTimeout(() => {
        onCloseHandler();
        invalidateReachQueries();
      }, 2000);
    },
    onError: (axiosErr: AxiosError<{ message?: string; data?: any }>) => {
      let message = t('errors.somethingWentWrong');

      if (axiosErr.response?.data?.message) {
        message = axiosErr.response?.data?.message;
      } else if (axiosErr.message) {
        message = axiosErr.message;
      }

      handleAmplitudeTrackingOnCreateCampaign({
        success: false,
        errorData: {
          message,
          ...(axiosErr.response?.data || {}),
        },
        fields,
        senderName,
      });

      notify.error(message, {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode:
              SYSTEM_EVENTS_GROUPING_CODES.REACH.CAMPAIGNS.CREATE_CAMPAIGN,
            msg: message,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.REACH.CAMPAIGNS,
          }),
      });
      
    }
  });

  const isCreateLoading = isPending || (isNotifyShown && notifyState === NOTIFICATION_STATUS.SUCCESS);

  useEffect(() => {
    setFields(INITIAL_ADD_EDIT_FIELDS_STATE(t));
  }, [lang]);

  return (
    <Modal
      containerRef={containerRef}
      width={isMobile ? '100%' : '38rem'}
      open={isModalOpen}
      isMobile={isMobile}
      closeOnClickOutside={false}
      hasCloseIcon
      onCloseHandler={onCloseHandler}
      title={t('innerLayout.reach.campaigns.createCampaign.title')}
      wrapperClassName={styles['reach-create-campaign']}
      btnsProps={[
        {
          color: 'primary',
          text: t('innerLayout.reach.campaigns.createCampaign.actions.create'),
          onClickHandler: handleAddEditCampaign,
          disabled: !isFormValid(fields) || isCreateLoading,
          loading: isPending,
        },
        {
          color: 'secondary',
          variant: 'outlined',
          text: t('innerLayout.reach.campaigns.createCampaign.actions.cancel'),
          onClickHandler: onCloseHandler,
          disabled: isCreateLoading,
        },
      ]}
      isOverlayAboveModal
    >
      <div className={styles['reach-create-campaign__body']}>
        <Input
          name={CREATE_CAMPAIGN_FIELDS.SENDER_NAME}
          label={t(
            'innerLayout.reach.campaigns.createCampaign.labels.senderId'
          )}
          disabled
          value={senderName}
        />
        <Input
          name={CREATE_CAMPAIGN_FIELDS.TITLE}
          label={t(
            'innerLayout.reach.campaigns.createCampaign.labels.campaignTitle'
          )}
          placeholder={t(
            'innerLayout.reach.campaigns.createCampaign.placeholders.campaignTitle'
          )}
          onChange={(e) => handleFieldChange({ event: e })}
          error={fields[CREATE_CAMPAIGN_FIELDS.TITLE].errorMsg}
          status={
            fields[CREATE_CAMPAIGN_FIELDS.TITLE].errorMsg ? 'error' : 'default'
          }
          value={fields[CREATE_CAMPAIGN_FIELDS.TITLE].value}
          disabled={isCreateLoading}
        />
        <UploadPhoneNumbers
          fields={fields}
          setFields={setFields}
          availableAmount={availableAmount}
          isModalOpen={isModalOpen}
          isCreateLoading={isCreateLoading}
        />
        <SmsDescription
          fields={fields}
          handleFieldChange={handleFieldChange}
          availableAmount={availableAmount}
          usedAmount={usedAmount}
          isCreateLoading={isCreateLoading}
        />
        <SmsContent
          fields={fields}
          handleFieldChange={handleFieldChange}
          isCreateLoading={isCreateLoading}
          isModalOpen={isModalOpen}
        />
        <DatePicker
          fields={fields}
          isCreateLoading={isCreateLoading}
          containerRef={containerRef}
          handleFieldChange={handleFieldChange}
          isModalOpen={isModalOpen}
        />
      </div>
    </Modal>
  );
};

export default CreateCampaign;
